import { useEffect, useRef, useState } from 'react';

export type ViewportHeightClass = 'sm' | 'md' | 'lg';

export interface ViewportSize {
  width: number;
  height: number;
  /** Height-based breakpoint: sm < 900 px, md 900–1080 px, lg > 1080 px */
  heightClass: ViewportHeightClass;
}

function getHeightClass(height: number): ViewportHeightClass {
  if (height < 900) return 'sm';
  if (height <= 1080) return 'md';
  return 'lg';
}

function readViewport(): ViewportSize {
  const width = typeof window !== 'undefined' ? window.innerWidth : 0;
  const height = typeof window !== 'undefined' ? window.innerHeight : 0;
  return { width, height, heightClass: getHeightClass(height) };
}

/**
 * Tracks viewport dimensions and derives a height-based breakpoint class.
 *
 * Breakpoints (height-based):
 *   - `'sm'` — height < 900 px   (laptop / smaller display)
 *   - `'md'` — height 900–1080 px (common 1080p monitor)
 *   - `'lg'` — height > 1080 px   (large / high-res display)
 *
 * Updates are debounced via `requestAnimationFrame` to avoid excessive
 * re-renders during resize events.
 *
 * Usage:
 * ```tsx
 * const { width, height, heightClass } = useViewportSize();
 * const isSmall = heightClass === 'sm';
 * ```
 */
export function useViewportSize(): ViewportSize {
  const [size, setSize] = useState<ViewportSize>(readViewport);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    function handleResize(): void {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        setSize(readViewport());
        rafRef.current = null;
      });
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return size;
}
